import { FC, useState, useEffect, useCallback } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import {
  PickingCart,
  PickingOrder,
  PickingOrderBoxes,
} from '../models/PickingModels';
import { getCartDetail, toggleLinePicked } from '../services/PickingCRUD';
import { LAST_LABEL_STORAGE_KEY } from '../constants/picking';
import { PickingProgressBar } from './PickingProgressBar';
import { PickingLineRow } from './PickingLineRow';
import { LabelPrintingPanel } from './LabelPrintingPanel';
import { ReprintLabelButton } from './ReprintLabelButton';

/** Convert an ISO date string (YYYY-MM-DD) to DD/MM/YYYY display format. */
function formatDateES(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

const BOX_SIZE_KEYS: Array<{
  field: keyof Pick<PickingOrderBoxes, 'small_box' | 'medium_box' | 'big_box'>;
  labelId: string;
}> = [
  { field: 'small_box', labelId: 'picking.smallBox' },
  { field: 'medium_box', labelId: 'picking.mediumBox' },
  { field: 'big_box', labelId: 'picking.bigBox' },
];

const COMPLETED_STATES: ReadonlyArray<PickingOrder['state']> = [
  'processing',
  'completed',
];

const OrderPickingScreen: FC = () => {
  const { cartCode } = useParams<{ cartCode: string }>();
  const history = useHistory();
  const intl = useIntl();
  const [cart, setCart] = useState<PickingCart | null>(null);
  const [currentOrderIndex, setCurrentOrderIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [togglingLineId, setTogglingLineId] = useState<number | null>(null);
  const [lastLabelUrl, setLastLabelUrl] = useState<string | null>(() =>
    localStorage.getItem(LAST_LABEL_STORAGE_KEY)
  );

  const saveLastLabelUrl = (url: string) => {
    localStorage.setItem(LAST_LABEL_STORAGE_KEY, url);
    setLastLabelUrl(url);
  };

  const fetchCart = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getCartDetail(cartCode);
      setCart(data);

      const firstPickingIndex = data.orders.findIndex(
        (o) => o.state === 'picking'
      );
      if (firstPickingIndex === -1) {
        toast.success(intl.formatMessage({ id: 'picking.allOrdersComplete' }));
        history.push('/picking');
        return;
      }
      setCurrentOrderIndex(firstPickingIndex);
    } catch (err) {
      console.error('Failed to fetch picking cart:', err);
      toast.error(intl.formatMessage({ id: 'picking.fetchOrderError' }));
      history.push('/picking');
    } finally {
      setLoading(false);
    }
  }, [cartCode, history, intl]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  const currentOrder: PickingOrder | null =
    cart?.orders?.[currentOrderIndex] ?? null;

  const isLinePicked = (lineId: number): boolean => {
    if (!currentOrder) return false;
    return currentOrder.picked_lines.some(
      (pl) => pl.order_line_id === lineId && pl.picked
    );
  };

  const allLinesPicked = currentOrder
    ? currentOrder.lines.every((line) => isLinePicked(line.id))
    : false;

  const handleToggleLine = async (lineId: number) => {
    if (!cart) return;
    setTogglingLineId(lineId);
    try {
      const { data: updatedLine } = await toggleLinePicked(cartCode, lineId);

      setCart((prev) => {
        if (!prev) return prev;
        const updatedOrders = prev.orders.map((order, idx) => {
          if (idx !== currentOrderIndex) return order;
          const existingIdx = order.picked_lines.findIndex(
            (pl) => pl.order_line_id === lineId
          );
          const updatedPickedLines = [...order.picked_lines];
          if (existingIdx >= 0) {
            updatedPickedLines[existingIdx] = updatedLine;
          } else {
            updatedPickedLines.push(updatedLine);
          }
          return { ...order, picked_lines: updatedPickedLines };
        });
        return { ...prev, orders: updatedOrders };
      });
    } catch (err) {
      console.error('Failed to toggle line picked:', err);
      toast.error(intl.formatMessage({ id: 'picking.markLineError' }));
    } finally {
      setTogglingLineId(null);
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div
          className="spinner-border text-primary"
          style={{ width: 48, height: 48 }}
        />
        <p className="mt-3 text-muted">
          {intl.formatMessage(
            { id: 'picking.loadingCart' },
            { code: cartCode }
          )}
        </p>
      </div>
    );
  }

  if (!cart || !currentOrder) {
    return null;
  }

  const completedOrders = cart.orders.filter((o) =>
    COMPLETED_STATES.includes(o.state)
  ).length;

  return (
    <div className="card p-4" style={{ maxWidth: 600, width: '100%' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          {intl.formatMessage({ id: 'picking.cart' }, { code: cart.code })}
        </h4>
        <button
          className="btn btn-sm btn-outline-secondary"
          onClick={() => history.push('/picking')}
        >
          {intl.formatMessage({ id: 'picking.changeCart' })}
        </button>
      </div>

      <PickingProgressBar
        current={completedOrders + 1}
        total={cart.orders.length}
      />

      {lastLabelUrl && (
        <ReprintLabelButton
          labelUrl={lastLabelUrl}
          className="btn btn-sm btn-info mb-3"
        />
      )}

      <div className="mb-3">
        <h5>
          {intl.formatMessage(
            { id: 'picking.orderTitle' },
            { code: currentOrder.reference }
          )}
          {currentOrder.baskets && (
            <span className="badge badge-light-primary fs-6 ms-2">
              {intl.formatMessage(
                { id: 'picking.baskets' },
                { range: currentOrder.baskets }
              )}
            </span>
          )}
        </h5>
        <span className="text-muted">
          {intl.formatMessage(
            { id: 'picking.promiseDate' },
            { date: formatDateES(currentOrder.promise_date) }
          )}
        </span>
        <div className="mt-2">
          <span className="text-muted fw-bold">
            {intl.formatMessage({ id: 'picking.boxesRecommended' })}:
          </span>
          <div className="d-flex flex-wrap gap-2 mt-1">
            {BOX_SIZE_KEYS.map(({ field, labelId }) =>
              currentOrder.boxes[field] > 0 ? (
                <span key={field} className="badge badge-light-info fs-7">
                  {currentOrder.boxes[field]}x{' '}
                  {intl.formatMessage({ id: labelId })}
                </span>
              ) : null
            )}
          </div>
        </div>
      </div>

      <div className="mb-3">
        {currentOrder.lines.map((line) => (
          <PickingLineRow
            key={line.id}
            line={line}
            isPicked={isLinePicked(line.id)}
            isLoading={togglingLineId === line.id}
            onToggle={handleToggleLine}
          />
        ))}
      </div>

      {allLinesPicked && (
        <LabelPrintingPanel
          cartCode={cartCode}
          orderId={currentOrder.id}
          defaultBoxes={currentOrder.boxes.total_boxes}
          onPrinted={fetchCart}
          onLabelGenerated={saveLastLabelUrl}
        />
      )}
    </div>
  );
};

export { OrderPickingScreen };
